import { useState, useEffect } from 'react';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const res = await fetch('https://formspree.io/f/xpqoynkl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: String(formData.get('name') ?? ''),
          email: String(formData.get('email') ?? ''),
          message: String(formData.get('message') ?? ''),
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead');
        }
      } else {
        const errorData = await res.json().catch(() => null);
        const errorMsg = errorData?.errors?.[0]?.message || '送信に失敗しました。もう一度お試しください。';
        setError(errorMsg);
      }
    } catch {
      setError('通信エラーが発生しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md bg-[#FAF8F5] rounded-lg overflow-hidden">
        <div className="px-8 pt-8 pb-4 flex items-start justify-between">
          <div>
            <p className="font-sans text-[#8FA68B] text-xs tracking-widest mb-2">SESSION BOOKING</p>
            <h2 className="font-serif text-[#3A3530] text-xl">体験セッションのお申し込み</h2>
          </div>
          <button
            onClick={onClose}
            className="cursor-pointer w-8 h-8 flex items-center justify-center text-[#8B8480] hover:text-[#3A3530] transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-8 pb-8">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(143, 166, 139, 0.2)' }}>
                <i className="ri-check-line text-[#8FA68B] text-2xl" />
              </div>
              <p className="font-serif text-[#3A3530] text-lg mb-3">お申し込みを受け付けました</p>
              <p className="font-sans text-[#6B6460] text-sm leading-relaxed">
                2〜3営業日以内にご連絡いたします。<br />
                ゆっくりお待ちください。
              </p>
              <button
                onClick={onClose}
                className="whitespace-nowrap cursor-pointer mt-6 px-8 py-3 font-sans text-sm text-white rounded-lg"
                style={{ backgroundColor: '#8FA68B' }}
              >
                閉じる
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} id="sanctuary-booking-form">
              <div className="space-y-4">
                <div>
                  <label className="font-sans text-[#3A3530] text-xs tracking-wider block mb-2">
                    お名前 <span className="text-[#C2A882]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg text-sm font-sans text-[#3A3530] outline-none transition-all"
                    style={{ backgroundColor: 'white', border: '1px solid #EDE7DC' }}
                    placeholder="山田 花子"
                  />
                </div>
                <div>
                  <label className="font-sans text-[#3A3530] text-xs tracking-wider block mb-2">
                    メールアドレス <span className="text-[#C2A882]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg text-sm font-sans text-[#3A3530] outline-none"
                    style={{ backgroundColor: 'white', border: '1px solid #EDE7DC' }}
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label className="font-sans text-[#3A3530] text-xs tracking-wider block mb-2">
                    今、いちばん気になっていること
                  </label>
                  <textarea
                    name="message"
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg text-sm font-sans text-[#3A3530] outline-none resize-none"
                    style={{ backgroundColor: 'white', border: '1px solid #EDE7DC' }}
                    placeholder="気になっていること、感じていることを自由にお書きください。(500文字以内)"
                  />
                </div>
              </div>

              {error && (
                <p className="font-sans text-xs text-red-500 mt-3">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="whitespace-nowrap cursor-pointer w-full mt-6 py-4 font-sans text-sm tracking-wider text-white rounded-lg transition-opacity disabled:opacity-60"
                style={{ backgroundColor: '#8FA68B' }}
              >
                {submitting ? '送信中...' : '申し込む'}
              </button>

              <p className="font-sans text-[#8B8480] text-xs text-center mt-4 leading-relaxed">
                ※ 申し込み後、2〜3営業日以内にご連絡します
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
